"use client";

import React, { useEffect, useState, useRef } from "react";
import Image from "next/image";
import style from "@/styles/Homepage/SuccessStories.module.scss";
import ResponsiveSwiper from "../ResponsiveSwiper";
import OrangeSpan from "@/Ui_Elements/OrangeSpan/OrangeSpan";
import { SwiperSlide } from "swiper/react"; // Important

const SuccessStories = ({ data }) => {
  const card = data?.card || [];
  const countersRef = useRef([]);
  const [counts, setCounts] = useState(card.map(() => "0"));
  const [isMobile, setIsMobile] = useState(false);
  const [chunkSize, setChunkSize] = useState(4);

  // 👉 Chunk array function
  const chunkArray = (arr, size) => {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  };

  const chunkedCards = chunkArray(card, chunkSize);

  useEffect(() => {
    if (!card.length) return;

    countersRef.current = countersRef.current.slice(0, card.length);

    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = countersRef.current.findIndex((el) => el === entry.target);
        const item = card[index];

        if (entry.isIntersecting && index !== -1 && item?.Counter && !entry.target.dataset.animated) {
          entry.target.dataset.animated = "true";

          const targetCount = parseInt(item.Counter.replace(/\D/g, ""), 10) || 0;
          const suffix = item.CounterNumber.replace(/\d+/g, "");

          let start = 0;
          const duration = 1500;
          const stepTime = Math.max(15, Math.floor(duration / targetCount));

          const updateCounter = () => {
            if (start < targetCount) {
              start += Math.ceil(targetCount / 100);
              if (start > targetCount) start = targetCount;
              setCounts((prevCounts) => {
                const newCounts = [...prevCounts];
                newCounts[index] = `${start}${suffix}`;
                return newCounts;
              });
              setTimeout(updateCounter, stepTime);
            }
          };

          updateCounter();
        }
      });
    }, observerOptions);

    setTimeout(() => {
      countersRef.current.forEach((el) => {
        if (el) observer.observe(el);
      });
    }, 500);

    return () => observer.disconnect();
  }, [card]);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        const width = window.innerWidth;
        setIsMobile(width <= 767);

        if (width <= 575) {
          setChunkSize(4);
        } else if (width > 575 && width <= 767) {
          setChunkSize(4);
        } else {
          setChunkSize(4); // desktop (no chunking needed but safe)
        }
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <section className={style["SuccessStories"]}>
      <div className="container">
        <div className="tx-center">
          <OrangeSpan text={data?.orangeSpanText} classname={style["HowVidyalayaOrangeSpan"]} />
          <h3 className="commonsectiontitle">{data?.sectiontitle}</h3>
        </div>

        <div className={style["mainbox"]}>
          {isMobile ? (
            // 👉 Mobile View: Swiper with 4 cards per slide
            <ResponsiveSwiper
              swiperConfig={{
                spaceBetween: 20,
                centeredSlides: false,
                slidesPerView: 1,
                loop: true,
                autoplay: {
                  delay: 70000,
                  disableOnInteraction: false,
                },
                navigation: true,
                className: "commonswiperclass prevnextpadding",
              }}
            >
              {chunkedCards.map((chunk, chunkIndex) => (
                <SwiperSlide key={chunkIndex}>
                  <div className={`${style["mainbox"]}`}>
                    {chunk.map((item, index) => {
                      const realIndex = chunkIndex * chunkSize + index;
                      return (
                        <div className={style["div-3"]} key={index} ref={(el) => (countersRef.current[realIndex] = el)}>
                          <div className={`${style["d-flex"]} ${style["success-stories-content"]}`}>
                            <Image
                              loading="lazy"
                              height={50}
                              width={50}
                              alt="VidyalayaImages"
                              src={item.image}
                              className={`img-fluid ${style["ic_success_stories"]}`}
                            />
                            <div className={`${style["ps-3"]} my-auto`}>
                              <p className={style["count"]}>
                                {item.Counter ? counts[realIndex] || item.CounterNumber : item.CounterNumber}
                              </p>
                              <p className={style["count-para"]}>{item.SubCounter}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </SwiperSlide>
              ))}
            </ResponsiveSwiper>
          ) : (
            // 👉 Desktop View: Normal Grid
            card.map((item, index) => (
              <div className={style["div-3"]} key={index} ref={(el) => (countersRef.current[index] = el)}>
                <div className={`${style["d-flex"]} ${style["success-stories-content"]}`}>
                  <Image
                    loading="lazy"
                    height={50}
                    width={50}
                    alt="VidyalayaImages"
                    src={item.image}
                    className={`img-fluid ${style["ic_success_stories"]}`}
                  />
                  <div className={`${style["ps-3"]} my-auto`}>
                    <p className={style["count"]}>
                      {item.Counter ? counts[index] || item.CounterNumber : item.CounterNumber}
                    </p>
                    <p className={style["count-para"]}>{item.SubCounter}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default SuccessStories;
